import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styles from "@/styles/components/ActionsManager.module.css";
import { Filter as FilterIcon, ChevronDown, ChevronUp } from "lucide-react";
// useAuth already imported once below (duplicate removed)
import { api } from "@/api";
import { useData } from "@/contexts/DataContext";
import { useAuth } from "@/contexts/AuthContext";
import { resolveApiBase } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Manager for actions (QR promos) with create + list + toggle active
const ActionsManager = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { actions, campaigns, createAction, updateAction, deleteAction } =
    useData();
  const { user } = useAuth();
  const isEmployee = user?.role === "EMPLOYEE";
  const [form, setForm] = useState({
    campaignId: "",
    title: "",
    discountType: "PERCENT",
    discountValue: "",
    maxRedemptions: "",
    actionStart: "",
    actionEnd: "",
    targetScans: "",
    targetRedeems: "",
  });
  const [creating, setCreating] = useState(false);
  // wizard state for create modal
  const [createStep, setCreateStep] = useState(1); // 1..5
  const [tab, setTab] = useState("active"); // 'active' | 'inactive'
  const [search, setSearch] = useState("");
  const [filterCampaignId, setFilterCampaignId] = useState("");
  const [filterType, setFilterType] = useState(""); // PERCENT | AMOUNT | ''
  const [filterFrom, setFilterFrom] = useState(""); // datetime-local
  const [filterTo, setFilterTo] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const hasFilters = !!(filterCampaignId || filterType || filterFrom || filterTo);

  const now = new Date();
  const toLocalInput = (d) => (d ? new Date(d).toISOString().slice(0, 16) : "");
  const daysRemaining = (d) => {
    if (!d) return null;
    const diff = new Date(d).getTime() - now.getTime();
    return Math.max(0, Math.ceil(diff / 86400000));
  };
  const isExpired = (a) => !!a.redeemEnd && new Date(a.redeemEnd) < now;
  const remainingFor = (a) => daysRemaining(a.redeemEnd || a.requestEnd);

  const activeList = useMemo(
    () => actions.filter((a) => a.active && !isExpired(a)),
    [actions]
  );
  const inactiveList = useMemo(
    () => actions.filter((a) => !a.active || isExpired(a)),
    [actions]
  );

  // Build campaignId -> name map early so it's initialized before any closures use it
  const grouped = useMemo(() => campaigns.reduce((acc, c) => {
    acc[c.id] = c.name;
    return acc;
  }, {}), [campaigns]);

  // Modal states declared early to avoid TDZ in effects
  const [qrPreview, setQrPreview] = useState(null);
  const closeQR = () => setQrPreview(null);
  const [embedFor, setEmbedFor] = useState(null); // action object
  const closeEmbed = () => setEmbedFor(null);
  const [editing, setEditing] = useState(null); // action object or null
  const [editForm, setEditForm] = useState(null);
  const closeEdit = () => {
    setEditing(null);
    setEditForm(null);
  };

  const matchesSearch = (a) => {
    if (!search) return true;
    const q = search.toLowerCase();
    const campaignName = grouped[a.campaignId]?.toLowerCase() || "";
    return (
      (a.title || "").toLowerCase().includes(q) ||
      (a.qrCodeSlug || "").toLowerCase().includes(q) ||
      campaignName.includes(q)
    );
  };
  const withinFilters = (a) => {
    if (filterCampaignId && a.campaignId !== filterCampaignId) return false;
    if (filterType && (a.discountType || "") !== filterType) return false;
    if (!filterFrom && !filterTo) return true;
    const start = new Date(a.redeemStart || a.requestStart || 0);
    const end = new Date(a.redeemEnd || a.requestEnd || 8640000000000000);
    const fromOk = filterFrom ? end >= new Date(filterFrom) : true;
    const toOk = filterTo ? start <= new Date(filterTo) : true;
    return fromOk && toOk;
  };
  const filteredActive = useMemo(
    () => activeList.filter(matchesSearch).filter(withinFilters),
    [activeList, search, campaigns, filterCampaignId, filterType, filterFrom, filterTo]
  );
  const filteredInactive = useMemo(
    () => inactiveList.filter(matchesSearch).filter(withinFilters),
    [inactiveList, search, campaigns, filterCampaignId, filterType, filterFrom, filterTo]
  );

  // Read hash for pre-filter (e.g., #actions-for=<campaignId>)
  React.useEffect(() => {
    function applyFromHash() {
      try {
        const h = window.location.hash || '';
        const m = h.match(/#actions-for=([^&]+)/);
        if (m && m[1] && campaigns.some(c => c.id === m[1])) {
          setFilterCampaignId(m[1]);
        }
      } catch(_) {}
    }
    applyFromHash();
    window.addEventListener('hashchange', applyFromHash);
    return () => window.removeEventListener('hashchange', applyFromHash);
  }, [campaigns]);

  // Close open modals with Escape key
  React.useEffect(() => {
    function onKeyDown(ev) {
      if (ev.key === 'Escape') {
        if (editing) return closeEdit();
        if (embedFor) return closeEmbed();
        if (qrPreview) return closeQR();
        if (creating) { setCreating(false); setCreateStep(1); }
      }
    }
    const anyOpen = !!(editing || embedFor || qrPreview || creating);
    if (anyOpen) window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [editing, embedFor, qrPreview, creating]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.campaignId || !form.title) return;
    const camp = campaigns.find((c) => c.id === form.campaignId);
    if (!camp || !camp.active) {
      alert(t('merchant.actions.errors.selectActiveCampaign'));
      return;
    }
    if (camp) {
      const rs = form.actionStart ? new Date(form.actionStart) : null;
      const re = form.actionEnd ? new Date(form.actionEnd) : null;
      if (rs && re && rs > re) {
        alert(t('merchant.actions.errors.endAfterStart'));
        return;
      }
      if (camp.startsAt && rs && rs < new Date(camp.startsAt)) {
        alert(t('merchant.actions.errors.beforeCampaignStart'));
        return;
      }
      if (camp.endsAt && re && re > new Date(camp.endsAt)) {
        alert(t('merchant.actions.errors.afterCampaignEnd'));
        return;
      }
    }
    await createAction({
      campaignId: form.campaignId,
      title: form.title,
      discountType: form.discountType,
      discountValue: form.discountValue
        ? parseFloat(form.discountValue)
        : undefined,
      maxRedemptions: form.maxRedemptions
        ? parseInt(form.maxRedemptions)
        : undefined,
    requestStart: form.actionStart || undefined,
    requestEnd: form.actionEnd || undefined,
    redeemStart: form.actionStart || undefined,
    redeemEnd: form.actionEnd || undefined,
      targetScans: form.targetScans ? parseInt(form.targetScans) : undefined,
      targetRedeems: form.targetRedeems
        ? parseInt(form.targetRedeems)
        : undefined,
    });
    setForm({
      campaignId: "",
      title: "",
      discountType: "PERCENT",
      discountValue: "",
      maxRedemptions: "",
      actionStart: "",
      actionEnd: "",
      targetScans: "",
      targetRedeems: "",
    });
    setCreating(false);
    setCreateStep(1);
  };

  // grouped already computed above

  const [openMenu, setOpenMenu] = useState({ id: null, scope: null });
  const openQR = async (slug) => {
    const data = await api.qrCode(slug);
    setQrPreview(data);
  };
  const isMenuOpen = (id, scope) => openMenu?.id === id && openMenu?.scope === scope;
  const closeMenu = () => setOpenMenu({ id: null, scope: null });

  // Embed snippet modal
  const [embedTab, setEmbedTab] = useState('scan'); // 'scan' | 'redeem' | 'verify' | 'claim' | 'qr' | 'link'
  // Use backend API base to construct embed origin (prevents using frontend dev host)
  // Prefer the centralized resolver to avoid localhost in production
  const EMBED_BASE = `${new URL(resolveApiBase()).origin}/api/public/embed`;
  const API_ORIGIN = `${new URL(resolveApiBase()).origin}`;

  const buildScanEmbed = (slug) => {
    const src = `${EMBED_BASE}/scan/${encodeURIComponent(slug)}`;
    return `<iframe src="${src}" style="width:100%;border:0;height:480px" scrolling="no" id="scanmirein-scan-${slug}"></iframe>
<script>
  (function(){
    function onMsg(ev){ try{ if(ev && ev.data && ev.data.type==='scanmirein:resize'){ var f = document.getElementById('scanmirein-scan-${slug}'); if(f){ f.style.height = (parseInt(ev.data.height,10)||400)+'px'; } } }catch(_){} }
    if(window.addEventListener) window.addEventListener('message', onMsg, false);
  })();
<\/script>`;
  };

  // QR image for the scan form URL (simple <img> embed)
  const buildQrImageEmbed = (slug) => {
    const formUrl = `${window.location.origin}/scan/${encodeURIComponent(slug)}`;
    const src = `${API_ORIGIN}/api/public/qr?data=${encodeURIComponent(formUrl)}&size=160`;
    return `<img src="${src}" alt="QR" width="160" height="160" />`;
  };

  // Direct link to the scan form (simple <a> embed)
  const buildFormLinkEmbed = (slug) => {
    const href = `${window.location.origin}/scan/${encodeURIComponent(slug)}`;
    return `<a href="${href}" target="_blank" rel="noopener">${t('merchant.actions.openAction') || 'Open Action'}</a>`;
  };

  // Register-only (no token/code UI inside iframe)
  const buildScanRegisterOnlyEmbed = (slug) => {
    const src = `${EMBED_BASE}/scan/${encodeURIComponent(slug)}?section=register`;
    return `<iframe src="${src}" style="width:100%;border:0;height:420px" scrolling="no" id="scanmirein-register-${slug}"></iframe>
<script>
  (function(){
    function onMsg(ev){ try{ if(ev && ev.data && ev.data.type==='scanmirein:resize'){ var f = document.getElementById('scanmirein-register-${slug}'); if(f){ f.style.height = (parseInt(ev.data.height,10)||360)+'px'; } } }catch(_){} }
    if(window.addEventListener) window.addEventListener('message', onMsg, false);
  })();
<\/script>`;
  };

  // Code-only display page (use verify/claim deep-links instead for full UX)
  // For backward compatibility, provide a builder to embed the claim page directly
  const buildRedeemEmbed = () => {
    const src = `${EMBED_BASE}/redeem`;
    return `<iframe src="${src}" style="width:100%;border:0;height:340px" scrolling="no" id="scanmirein-redeem"></iframe>
<script>
  (function(){
    function onMsg(ev){ try{ if(ev && ev.data && ev.data.type==='scanmirein:resize'){ var f = document.getElementById('scanmirein-redeem'); if(f){ f.style.height = (parseInt(ev.data.height,10)||340)+'px'; } } }catch(_){} }
    if(window.addEventListener) window.addEventListener('message', onMsg, false);
  })();
<\/script>`;
  };

  // Code-only embeds (templates): verify via token, or display existing code via claim
  const buildVerifyEmbedTemplate = () => {
    const src = `${EMBED_BASE}/verify/YOUR_TOKEN_HERE`;
    return `<iframe src="${src}" style="width:100%;border:0;height:380px" scrolling="no" id="scanmirein-verify"></iframe>
<script>
  (function(){
    function onMsg(ev){ try{ if(ev && ev.data && ev.data.type==='scanmirein:resize'){ var f = document.getElementById('scanmirein-verify'); if(f){ f.style.height = (parseInt(ev.data.height,10)||360)+'px'; } } }catch(_){} }
    if(window.addEventListener) window.addEventListener('message', onMsg, false);
  })();
<\/script>`;
  };
  const buildClaimEmbedTemplate = () => {
    const src = `${EMBED_BASE}/claim/YOUR_CODE_HERE`;
    return `<iframe src="${src}" style="width:100%;border:0;height:380px" scrolling="no" id="scanmirein-claim"></iframe>
<script>
  (function(){
    function onMsg(ev){ try{ if(ev && ev.data && ev.data.type==='scanmirein:resize'){ var f = document.getElementById('scanmirein-claim'); if(f){ f.style.height = (parseInt(ev.data.height,10)||360)+'px'; } } }catch(_){} }
    if(window.addEventListener) window.addEventListener('message', onMsg, false);
  })();
<\/script>`;
  };

  // Edit modal
  const openEdit = (a) => {
    setEditing(a);
    setEditForm({
      title: a.title || "",
      discountType: a.discountType || "PERCENT",
      discountValue: a.discountValue ?? "",
      maxRedemptions: a.maxRedemptions ?? "",
      actionStart: toLocalInput(a.redeemStart || a.requestStart),
      actionEnd: toLocalInput(a.redeemEnd || a.requestEnd),
      targetScans: a.targetScans ?? "",
      targetRedeems: a.targetRedeems ?? "",
      capiEnabled: (a.capiEnabled ?? true),
      capiPixelIdOverride: a.capiPixelIdOverride || "",
      capiTestCodeOverride: a.capiTestCodeOverride || "",
      capiConversionValueOverride: (a.capiConversionValueOverride ?? ""),
      leadEventNameOverride: a.leadEventNameOverride || "",
    });
  };
  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editing || !editForm) return;
    await updateAction(editing.id, {
      title: editForm.title,
      discountType: editForm.discountType,
      discountValue:
        editForm.discountValue !== "" && editForm.discountValue !== null
          ? parseFloat(editForm.discountValue)
          : undefined,
      maxRedemptions:
        editForm.maxRedemptions !== "" && editForm.maxRedemptions !== null
          ? parseInt(editForm.maxRedemptions)
          : undefined,
      requestStart: editForm.actionStart || undefined,
      requestEnd: editForm.actionEnd || undefined,
      redeemStart: editForm.actionStart || undefined,
      redeemEnd: editForm.actionEnd || undefined,
      targetScans:
        editForm.targetScans !== "" && editForm.targetScans !== null
          ? parseInt(editForm.targetScans)
          : undefined,
      targetRedeems:
        editForm.targetRedeems !== "" && editForm.targetRedeems !== null
          ? parseInt(editForm.targetRedeems)
          : undefined,
      capiEnabled: !!editForm.capiEnabled,
      capiPixelIdOverride: editForm.capiPixelIdOverride || null,
      capiTestCodeOverride: editForm.capiTestCodeOverride || null,
      capiConversionValueOverride:
        editForm.capiConversionValueOverride === "" || editForm.capiConversionValueOverride === null
          ? null
          : parseFloat(editForm.capiConversionValueOverride),
      leadEventNameOverride: editForm.leadEventNameOverride || null,
    });
    setEditing(null);
    setEditForm(null);
  };

  // Render
  return (
    <div className="p-3">
      {/* Header: tabs, search, create */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <div className="inline-flex border rounded overflow-hidden">
            <button
              type="button"
              className={`px-3 py-1 text-xs ${tab === 'active' ? 'bg-blue-600 text-white' : 'bg-background'}`}
              onClick={() => setTab('active')}
            >
              {t('merchant.actions.tabs.active')}
            </button>
            <button
              type="button"
              className={`px-3 py-1 text-xs border-l ${tab === 'inactive' ? 'bg-blue-600 text-white' : 'bg-background'}`}
              onClick={() => setTab('inactive')}
            >
              {t('merchant.actions.tabs.inactive')}
            </button>
          </div>
          <input
            className="border px-2 py-1 rounded text-xs"
            placeholder={t('merchant.actions.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {!isEmployee && (
          <button
            type="button"
            className="text-xs bg-blue-600 text-white px-3 py-1 rounded"
            onClick={() => { setCreating(true); setCreateStep(1); }}
          >
            {t('merchant.actions.newAction')}
          </button>
        )}
      </div>
      {creating && !isEmployee && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { setCreating(false); setCreateStep(1); }}>
          <form onSubmit={submit} className="bg-background p-6 rounded shadow max-w-2xl w-full space-y-4" onClick={(e)=> e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-sm">{t('merchant.actions.create.title')}</h4>
              <div className="text-[11px] text-muted-foreground">{t('merchant.actions.create.stepOf', { step: createStep, total: 5 })}</div>
            </div>

            {/* Stepper */}
            <ol className="flex items-center gap-2 text-[11px]" aria-label={t('merchant.actions.create.stepsAria')}>
              {[1,2,3,4,5].map((n) => (
                <li key={n} className={`px-2 py-1 rounded ${createStep===n? 'bg-blue-600 text-white':'bg-muted text-foreground'}`}> {n} </li>
              ))}
            </ol>

            {/* Steps */}
            {createStep === 1 && (
              <div className="space-y-3 text-xs">
                <div className="text-muted-foreground">{t('merchant.actions.create.s1.help')}</div>
                <div className="grid md:grid-cols-2 gap-3 items-end">
                  <div className="space-y-1">
                    <label className="text-[11px] text-muted-foreground">{t('merchant.actions.create.s1.campaign')} <span className="text-red-600">*</span></label>
                    <select value={form.campaignId} onChange={(e)=>setForm(f=>({...f, campaignId:e.target.value}))} className="border px-2 py-1 rounded w-full" required>
                      <option value="">{t('merchant.actions.create.choose')}</option>
                      {campaigns.filter(c=>c.active).map(c=> (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[11px] text-muted-foreground">{t('merchant.actions.create.s1.title')} <span className="text-red-600">*</span></label>
                    <input className="border px-2 py-1 rounded w-full" placeholder={t('merchant.actions.create.s1.titlePh')} value={form.title} onChange={(e)=>setForm(f=>({...f, title:e.target.value}))} required />
                  </div>
                </div>
              </div>
            )}

            {createStep === 2 && (
              <div className="space-y-3 text-xs">
                <div className="text-muted-foreground">{t('merchant.actions.create.s2.help')}</div>
                <div className="grid md:grid-cols-2 gap-3 items-end">
                  <div className="space-y-1">
                    <label className="text-[11px] text-muted-foreground">{t('merchant.actions.create.s2.discountType')}</label>
                    <select value={form.discountType} onChange={(e)=>setForm(f=>({...f, discountType:e.target.value}))} className="border px-2 py-1 rounded w-full">
                      <option value="PERCENT">{t('merchant.actions.create.s2.percent')}</option>
                      <option value="AMOUNT">{t('merchant.actions.create.s2.amount')}</option>
                      <option value="UNIT">{t('merchant.actions.create.s2.unit')}</option>
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[11px] text-muted-foreground">{t('merchant.actions.create.s2.discountValue')}</label>
                    <input type="number" step="0.01" className="border px-2 py-1 rounded w-full" placeholder={t('merchant.actions.create.s2.valuePh')} value={form.discountValue} onChange={(e)=>setForm(f=>({...f, discountValue:e.target.value}))} />
                  </div>
                </div>
              </div>
            )}

            {createStep === 3 && (()=>{
              const camp = campaigns.find(c=>c.id===form.campaignId);
              const cStart = camp?.startsAt ? new Date(camp.startsAt) : null;
              const cEnd = camp?.endsAt ? new Date(camp.endsAt) : null;
              const aStart = form.actionStart ? new Date(form.actionStart) : null;
              const aEnd = form.actionEnd ? new Date(form.actionEnd) : null;
              const tooEarly = !!(cStart && aStart && aStart < cStart);
              const tooLate = !!(cEnd && aEnd && aEnd > cEnd);
              const badOrder = !!(aStart && aEnd && aStart > aEnd);
              const fmt = (d)=> d? new Date(d).toLocaleString() : '-';
              return (
                <div className="space-y-3 text-xs">
                  <div className="text-muted-foreground">{t('merchant.actions.create.s3.help')}</div>
                  <div className="grid md:grid-cols-2 gap-3 items-end">
                    <div className="space-y-1 md:col-span-2">
                      <label className="text-[11px] text-muted-foreground">{t('merchant.actions.create.s3.period')}</label>
                      <div className="grid grid-cols-2 gap-2">
                        <input type="datetime-local" value={form.actionStart} onChange={(e)=>setForm(f=>({...f, actionStart:e.target.value}))} className="border px-2 py-1 rounded w-full" />
                        <input type="datetime-local" value={form.actionEnd} onChange={(e)=>setForm(f=>({...f, actionEnd:e.target.value}))} className="border px-2 py-1 rounded w-full" />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <label className="text-[11px] text-muted-foreground">{t('merchant.actions.create.s3.maxRedeems')}</label>
                      <input type="number" className="border px-2 py-1 rounded w-full" placeholder={t('merchant.actions.create.s3.maxRedeemsPh')} value={form.maxRedemptions} onChange={(e)=>setForm(f=>({...f, maxRedemptions:e.target.value}))} />
                    </div>
                  </div>
                  {(badOrder || tooEarly || tooLate) && (
                    <div className={`p-2 rounded border text-[11px] ${badOrder? 'bg-red-50 border-red-300 text-red-800' : 'bg-amber-50 border-amber-300 text-amber-800'}`}>
                      {badOrder && <div>{t('merchant.actions.create.s3.errors.endAfterStart')}</div>}
                      {tooEarly && <div>{t('merchant.actions.create.s3.errors.beforeCampaignStart', { date: fmt(cStart) })}</div>}
                      {tooLate && <div>{t('merchant.actions.create.s3.errors.afterCampaignEnd', { date: fmt(cEnd) })}</div>}
                      {t('merchant.actions.create.s3.errors.adjustPeriod')}
                    </div>
                  )}
                </div>
              );
            })()}

            {createStep === 4 && (
              <div className="space-y-3 text-xs">
                <div className="text-muted-foreground">{t('merchant.actions.create.s4.help')}</div>
                <div className="grid md:grid-cols-2 gap-3 items-end">
                  <div className="space-y-1">
                    <label className="text-[11px] text-muted-foreground">{t('merchant.actions.create.s4.targetScans')}</label>
                    <input type="number" className="border px-2 py-1 rounded w-full" value={form.targetScans} onChange={(e)=>setForm(f=>({...f, targetScans:e.target.value}))} />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[11px] text-muted-foreground">{t('merchant.actions.create.s4.targetRedeems')}</label>
                    <input type="number" className="border px-2 py-1 rounded w-full" value={form.targetRedeems} onChange={(e)=>setForm(f=>({...f, targetRedeems:e.target.value}))} />
                  </div>
                </div>
              </div>
            )}

            {createStep === 5 && (
              <div className="space-y-3 text-xs">
                <div className="text-muted-foreground">{t('merchant.actions.create.s5.help')}</div>
                <div className="bg-muted border rounded p-3">
                  <dl className="grid md:grid-cols-2 gap-2">
                    <div>
                      <dt className="text-muted-foreground">{t('merchant.actions.summary.campaign')}</dt>
                      <dd className="font-medium">{grouped[form.campaignId] || '-'}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">{t('common.title')}</dt>
                      <dd className="font-medium">{form.title || '-'}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Rabatt</dt>
                      <dd className="font-medium">
                        {form.discountType === 'AMOUNT'
                          ? `Betrag ${form.discountValue || '-'}`
                          : form.discountType === 'UNIT'
                          ? `Stück ${form.discountValue || '-'}`
                          : `Prozent ${form.discountValue || '-'}`}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Max Einlösungen</dt>
                      <dd className="font-medium">{form.maxRedemptions || '-'}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Zeitraum</dt>
                      <dd className="font-medium">{form.actionStart || form.actionEnd ? `${form.actionStart || '–'} — ${form.actionEnd || '–'}` : '-'}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Ziele</dt>
                      <dd className="font-medium">Scans: {form.targetScans || '-'}, Einlösungen: {form.targetRedeems || '-'}</dd>
                    </div>
                  </dl>
                </div>
              </div>
            )}

            <div className="flex justify-between items-center gap-3">
              <button type="button" onClick={() => { setCreating(false); setCreateStep(1); }} className="text-xs border px-3 py-1 rounded">{t('common.cancel')}</button>
              <div className="flex gap-2">
                {createStep > 1 && (
                  <button type="button" className="text-xs border px-3 py-1 rounded" onClick={()=> setCreateStep((s)=> Math.max(1, s-1))}>{t('common.back')}</button>
                )}
                {createStep < 5 && (
                  <button type="button" className="text-xs bg-blue-600 text-white px-3 py-1 rounded" onClick={()=>{
                    // simple per-step validation
                    if (createStep === 1) {
                      if (!form.campaignId || !form.title) return;
                    }
                    if (createStep === 3) {
                      const camp = campaigns.find(c=>c.id===form.campaignId);
                      const cStart = camp?.startsAt ? new Date(camp.startsAt) : null;
                      const cEnd = camp?.endsAt ? new Date(camp.endsAt) : null;
                      const aStart = form.actionStart ? new Date(form.actionStart) : null;
                      const aEnd = form.actionEnd ? new Date(form.actionEnd) : null;
                      if (aStart && aEnd && aStart > aEnd) return; // bad order
                      if (cStart && aStart && aStart < cStart) return;
                      if (cEnd && aEnd && aEnd > cEnd) return;
                    }
                    setCreateStep((s)=> Math.min(5, s+1));
                  }}>{t('common.next')}</button>
                )}
                {createStep === 5 && (()=>{
                  const camp = campaigns.find(c=>c.id===form.campaignId);
                  const cStart = camp?.startsAt ? new Date(camp.startsAt) : null;
                  const cEnd = camp?.endsAt ? new Date(camp.endsAt) : null;
                  const aStart = form.actionStart ? new Date(form.actionStart) : null;
                  const aEnd = form.actionEnd ? new Date(form.actionEnd) : null;
                  const badOrder = !!(aStart && aEnd && aStart > aEnd);
                  const tooEarly = !!(cStart && aStart && aStart < cStart);
                  const tooLate = !!(cEnd && aEnd && aEnd > cEnd);
                  const disabled = badOrder || tooEarly || tooLate;
                  return (
                    <button className="text-xs bg-blue-600 text-white px-3 py-1 rounded disabled:opacity-50" type="submit" disabled={disabled}>{t('common.save')}</button>
                  );
                })()}
              </div>
            </div>
          </form>
        </div>
      )}
      {/* Mobile cards (only on phones) */}
      <div className="sm:hidden mt-3 space-y-3">
        {(tab==='active' ? filteredActive : filteredInactive).map((a) => (
          <div key={a.id} className="border rounded p-3 bg-background">
            <div className="flex justify-between items-start gap-3">
              <div className="min-w-0">
                <div className="font-medium text-sm truncate">{a.title}</div>
                <div className="text-[11px] text-muted-foreground truncate">{grouped[a.campaignId] || '-'}</div>
              </div>
              <div>
                {isExpired(a) ? (
                  <span className="px-2 py-1 rounded-full text-[10px] bg-muted text-foreground">{t('merchant.campaigns.filters.expired')}</span>
                ) : (
                  <span className={`px-2 py-1 rounded-full text-[10px] ${a.active? 'bg-green-100 text-green-800':'bg-muted text-foreground'}`}>{a.active? t('merchant.campaigns.filters.active'):t('merchant.campaigns.status.inactive')}</span>
                )}
              </div>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
              <div>
                <div className="text-muted-foreground">Typ/Wert</div>
                <div className="font-mono">{a.discountType} {a.discountValue ?? '-'}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Max</div>
                <div>{a.maxRedemptions ?? '-'}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Ziele</div>
                <div>Scans: {a.targetScans ?? '-'} · Einl.: {a.targetRedeems ?? '-'}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Resttage</div>
                <div>{remainingFor(a) == null ? '-' : remainingFor(a)}</div>
              </div>
            </div>
            {(a.requestStart || a.redeemStart || a.requestEnd || a.redeemEnd) && (
              <div className="mt-2 text-[11px] text-foreground">
                Zeitraum: { (a.redeemStart || a.requestStart) ? new Date(a.redeemStart || a.requestStart).toLocaleDateString() : '–' } { (a.redeemEnd || a.requestEnd) ? '– ' + new Date(a.redeemEnd || a.requestEnd).toLocaleDateString() : '' }
              </div>
            )}
            {a.targetScans || a.targetRedeems ? (
              <div className="mt-2">
                <div className="w-full h-2 bg-muted rounded overflow-hidden">
                  <div
                    className="h-full bg-blue-500"
                    style={{
                      width: `${Math.min(100, Math.max(
                        a.targetScans ? (((a.holdersCount ?? (a._count?.scan ?? 0)) || 0) / a.targetScans) * 100 : 0,
                        a.targetRedeems ? (((a.redeemedCount ?? (a._count?.redeem ?? 0)) || 0) / a.targetRedeems) * 100 : 0
                      ))}%`,
                    }}
                  />
                </div>
              </div>
            ) : null}
            <div className="mt-3 grid grid-cols-2 gap-2 items-center">
              <div className="truncate">
                <div className="text-[11px] text-muted-foreground">Slug</div>
                <button className="underline text-left break-all" onClick={() => openQR(a.qrCodeSlug)}>
                  {a.qrCodeSlug}
                </button>
              </div>
              <div className="justify-self-end text-right flex items-center gap-3">
                <button
                  onClick={() => {
                    const formUrl = window.location.origin + "/scan/" + a.qrCodeSlug;
                    const qrUrl = `${API_ORIGIN}/api/public/qr?data=${encodeURIComponent(formUrl)}&size=320`;
                    window.open(qrUrl, '_blank');
                  }}
                  className="text-[11px] underline"
                  title="QR-Bild anzeigen"
                >
                  QR
                </button>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.origin + "/scan/" + a.qrCodeSlug);
                  }}
                  className="text-[11px] underline"
                  title="Formular-Link kopieren"
                >
                  Copy Formular
                </button>
              </div>
            </div>
            {!isEmployee && (
              <div className="mt-3">
                <Popover
                  open={isMenuOpen(a.id, 'cards')}
                  onOpenChange={(open) => setOpenMenu(open ? { id: a.id, scope: 'cards' } : { id: null, scope: null })}
                >
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="border px-2 py-1 rounded text-xs"
                      aria-haspopup="true"
                      aria-expanded={isMenuOpen(a.id, 'cards')}
                      title={t('merchant.campaigns.actions')}
                    >
                      ⚙️ {t('merchant.campaigns.actions')}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="end"
                    sideOffset={6}
                    className="w-40 p-0"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                  >
                    <div className="flex flex-col">
                      <button className="w-full text-left px-3 py-2 text-xs hover:bg-muted" onClick={() => { closeMenu(); setEmbedFor(a); }}>Embed</button>
                      <button className="w-full text-left px-3 py-2 text-xs hover:bg-muted" onClick={() => { closeMenu(); openEdit(a); }}>{t('merchant.campaigns.edit')}</button>
                      <button className="w-full text-left px-3 py-2 text-xs hover:bg-muted" onClick={() => { closeMenu(); navigate(`/actions/${a.id}/design`); }}>Design</button>
                      {tab === 'active' ? (
                        <button className="w-full text-left px-3 py-2 text-xs hover:bg-muted" onClick={() => { closeMenu(); updateAction(a.id, { active: false }); }}>{t('merchant.campaigns.deactivate')}</button>
                      ) : (
                        <button className="w-full text-left px-3 py-2 text-xs hover:bg-muted" onClick={() => { closeMenu(); updateAction(a.id, { active: true }); }}>{t('merchant.campaigns.activate')}</button>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            )}
          </div>
        ))}
        {(tab==='active' ? filteredActive : filteredInactive).length === 0 && (
          <div className="text-center text-muted-foreground text-xs py-6">{t('redeemer.noneRunning')}</div>
        )}
      </div>

      {/* Desktop table (hidden on phones) */}
      <div className="hidden sm:block overflow-x-auto mt-4">
      <table className="w-full text-xs table-fixed">
        <thead>
          <tr className="border-b text-left text-[11px] text-muted-foreground bg-muted">
            <th className="py-2 px-2 w-[26%]">{t('merchant.actions.create.s1.title')}</th>
            <th className="py-2 px-2 w-[22%]">{t('merchant.actions.create.s1.campaign')}</th>
            <th className="py-2 px-2 w-[12%]">{t('merchant.actions.create.s2.discountType')}</th>
            <th className="py-2 px-2 w-[14%]">{t('common.goals', 'Goals')}</th>
            <th className="py-2 px-2 w-[12%]">{t('merchant.analytics.period')}</th>
            <th className="py-2 px-2 w-[6%]">{t('sendmirein.history.detail.progress')}</th>
            <th className="py-2 px-2 w-[8%]">{t('merchant.leads.status')}</th>
            <th className="py-2 px-2 w-[6%]" />
          </tr>
        </thead>
        <tbody>
          {(tab==='active' ? filteredActive : filteredInactive).map((a) => (
            <tr key={a.id} className="border-b odd:bg-background even:bg-muted hover:bg-muted">
              <td className="py-2 px-2 align-top">
                <div className="font-medium max-w-[260px] truncate" title={a.title}>{a.title}</div>
                <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground max-w-[260px]">
                  <button
                    onClick={() => {
                      const formUrl = window.location.origin + "/scan/" + a.qrCodeSlug;
                      const qrUrl = `${API_ORIGIN}/api/public/qr?data=${encodeURIComponent(formUrl)}&size=320`;
                      window.open(qrUrl, '_blank');
                    }}
                    className="underline"
                    title="QR-Bild anzeigen"
                  >
                    QR
                  </button>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(
                        window.location.origin + "/scan/" + a.qrCodeSlug
                      );
                    }}
                    className="underline"
                    title="Formular-Link kopieren"
                  >
                    Copy
                  </button>
                  <span className="font-mono truncate" title={a.qrCodeSlug}>{a.qrCodeSlug}</span>
                </div>
              </td>
              <td className="py-2 px-2 align-top max-w-[220px] truncate" title={grouped[a.campaignId] || "-"}>{grouped[a.campaignId] || "-"}</td>
              <td className="py-2 px-2 align-top text-[11px] text-foreground whitespace-nowrap">
                <div>{a.discountType || '-'}</div>
                <div className="text-muted-foreground">{a.discountValue ?? '-'}</div>
              </td>
              <td className="py-2 px-2 align-top text-[11px] text-foreground">
                {(() => {
                  const scans = (a.holdersCount ?? (a._count?.scan ?? 0)) || 0;
                  const redeems = (a.redeemedCount ?? (a._count?.redeem ?? 0)) || 0;
                  const scansLabel = t('redeemer.labels')?.scans ? t('redeemer.labels.scans') : 'Scans';
                  const redeemsLabel = t('redeemer.labels')?.redeems ? t('redeemer.labels.redeems') : 'Redeems';
                  return (
                    <>
                      <div className="whitespace-nowrap">Max: {a.maxRedemptions ?? '-'}</div>
                      <div className="whitespace-nowrap">
                        {scansLabel}: {scans}{a.targetScans ? ` / ${a.targetScans}` : ''}
                      </div>
                      <div className="whitespace-nowrap">
                        {redeemsLabel}: {redeems}{a.targetRedeems ? ` / ${a.targetRedeems}` : ''}
                      </div>
                    </>
                  );
                })()}
              </td>
              <td className="py-2 px-2 text-[11px] text-muted-foreground whitespace-nowrap align-top">
                {(a.requestStart || a.redeemStart || a.requestEnd || a.redeemEnd) ? (
                  <>
                    <div>
                      { (a.redeemStart || a.requestStart) ? new Date(a.redeemStart || a.requestStart).toLocaleDateString() : '–' } { (a.redeemEnd || a.requestEnd) ? '– ' + new Date(a.redeemEnd || a.requestEnd).toLocaleDateString() : '' }
                    </div>
                    <div className="text-muted-foreground">
                      {remainingFor(a) == null
                        ? '-'
                        : `${remainingFor(a)} ${t('merchant.analytics.daysRemaining', 'Tage übrig')}`}
                    </div>
                  </>
                ) : '-' }
              </td>
              <td className="py-2 px-2 align-top">
                {a.targetScans || a.targetRedeems ? (
                  <div className="space-y-1">
                    <div className="w-24 h-2 bg-muted rounded overflow-hidden">
                      <div
                        className="h-full bg-blue-500"
                        style={{
                          width: `${Math.min(
                            100,
                            Math.max(
                              a.targetScans
                                ? (((a.holdersCount ?? (a._count?.scan ?? 0)) || 0) / a.targetScans) * 100
                                : 0,
                              a.targetRedeems
                                ? (((a.redeemedCount ?? (a._count?.redeem ?? 0)) || 0) / a.targetRedeems) * 100
                                : 0
                            )
                          )}%`,
                        }}
                      />
                    </div>
                    <div className="text-[11px] text-muted-foreground whitespace-nowrap">
                      {(() => {
                        const scans = (a.holdersCount ?? (a._count?.scan ?? 0)) || 0;
                        const redeems = (a.redeemedCount ?? (a._count?.redeem ?? 0)) || 0;
                        return t('redeemer.labels')?.scans
                          ? `${t('redeemer.labels.scans')}: ${scans} · ${t('redeemer.labels.redeems')}: ${redeems}`
                          : `Scans: ${scans} · Redeems: ${redeems}`;
                      })()}
                    </div>
                  </div>
                ) : (
                  "-"
                )}
              </td>
              <td className="py-2 px-2">
                {isExpired(a) ? (
                  <span className="px-2 py-1 rounded-full text-xs bg-muted text-foreground">{t('merchant.campaigns.filters.expired')}</span>
                ) : (
                  <span className={`px-2 py-1 rounded-full text-xs ${a.active? 'bg-green-100 text-green-800':'bg-muted text-foreground'}`}>{a.active? t('merchant.campaigns.filters.active'):t('merchant.campaigns.status.inactive')}</span>
                )}
              </td>
              <td className="py-2 px-2 text-right relative">
                {!isEmployee && (
                  <div className="inline-block text-left">
                    <Popover
                      open={isMenuOpen(a.id, 'table')}
                      onOpenChange={(open) => setOpenMenu(open ? { id: a.id, scope: 'table' } : { id: null, scope: null })}
                    >
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="border px-2 py-1 rounded text-xs"
                          aria-haspopup="true"
                          aria-expanded={isMenuOpen(a.id, 'table')}
                          title="Aktionen"
                        >
                          <span aria-hidden>⚙️</span>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        align="end"
                        sideOffset={6}
                        className="w-40 p-0"
                        onOpenAutoFocus={(e) => e.preventDefault()}
                      >
                        <div className="flex flex-col">
                          <button
                            className="w-full text-left px-3 py-2 text-xs hover:bg-muted"
                            onClick={() => { closeMenu(); setEmbedFor(a); }}
                          >
                            Embed
                          </button>
                          <button
                            className="w-full text-left px-3 py-2 text-xs hover:bg-muted"
                            onClick={() => { closeMenu(); openEdit(a); }}
                          >
                            Bearbeiten
                          </button>
                          <button
                            className="w-full text-left px-3 py-2 text-xs hover:bg-muted"
                            onClick={() => {
                              closeMenu();
                              // Navigate to new design UI (register/code builder)
                              navigate(`/actions/${a.id}/design`);
                            }}
                          >
                            Design
                          </button>
                          <button
                            className="w-full text-left px-3 py-2 text-xs hover:bg-muted"
                            onClick={() => {
                              closeMenu();
                              // Navigate to design UI with landing tab active
                              navigate(`/actions/${a.id}/design#landing`);
                            }}
                          >
                            Landingpage
                          </button>
                          {tab === 'active' ? (
                            <button
                              className="w-full text-left px-3 py-2 text-xs hover:bg-muted"
                              onClick={() => { closeMenu(); updateAction(a.id, { active: false }); }}
                            >
                              Deaktivieren
                            </button>
                          ) : (
                            <button
                              className="w-full text-left px-3 py-2 text-xs hover:bg-muted"
                              onClick={() => { closeMenu(); updateAction(a.id, { active: true }); }}
                            >
                              Aktivieren
                            </button>
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                )}
              </td>
            </tr>
          ))}
          {(tab==='active' ? filteredActive : filteredInactive).length === 0 && (
            <tr>
              <td colSpan={8} className="py-4 text-center text-muted-foreground">
                Keine Aktionen
              </td>
            </tr>
          )}
        </tbody>
  </table>
  </div>
      {qrPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={closeQR}>
          <div className="bg-background p-6 rounded shadow max-w-sm w-full space-y-4 text-center" onClick={(e)=> e.stopPropagation()}>
            <h4 className="font-semibold text-sm">QR Vorschau</h4>
            <img src={qrPreview.qr} alt="QR" className="mx-auto" />
            <p className="text-xs break-all">{qrPreview.url}</p>
            <div className="flex justify-center gap-2">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(qrPreview.url);
                }}
                className="text-xs border px-3 py-1 rounded"
              >
                Link kopieren
              </button>
              <button
                onClick={closeQR}
                className="text-xs border px-3 py-1 rounded"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      {embedFor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={closeEmbed}>
          <div className="bg-background rounded shadow max-w-2xl w-full overflow-hidden" onClick={(e)=> e.stopPropagation()}>
            {/* Sticky Header with Close */}
            <div className="sticky top-0 z-10 bg-background border-b px-4 py-3 flex items-center justify-between">
              <div>
                <h4 className="font-semibold text-sm">Einbetten (Iframe)</h4>
                <p className="text-[11px] text-muted-foreground">Diese Snippets in Ihre Website einfügen. Aktion: <span className="font-mono">{embedFor.qrCodeSlug}</span></p>
              </div>
              <button onClick={closeEmbed} className="text-xs border px-3 py-1 rounded">{t('common.close')}</button>
            </div>
            {/* Tabs */}
            <div className="px-4 pt-3">
              <div className="flex items-center gap-2 text-xs">
                {['scan','redeem','verify','claim','qr','link'].map((t) => (
                  <button key={t} onClick={() => setEmbedTab(t)} className={`px-3 py-1 rounded border ${embedTab === t ? 'bg-blue-600 text-white border-blue-600' : 'bg-background'}`}>
                    {t === 'scan' ? 'Scan/Register' : t === 'redeem' ? 'Einlösen' : t === 'verify' ? 'Verify (Token)' : t === 'claim' ? 'Claim (Code)' : t === 'qr' ? 'QR‑Bild' : 'Form‑Link'}
                  </button>
                ))}
              </div>
            </div>
            {/* Body scroll area */}
            <div className="p-4 max-h-[70vh] overflow-auto space-y-4">
              {embedTab === 'scan' && (
                <div>
                  <div className="text-xs font-semibold mb-2">Scan/Register Formular</div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <div className="text-[11px] text-muted-foreground mb-1">Komplett (Scan + ggf. Token/Code)</div>
                      <textarea readOnly className="w-full h-32 border rounded p-2 text-[11px] font-mono" value={buildScanEmbed(embedFor.qrCodeSlug)} />
                      <div className="mt-2 flex gap-2">
                        <button className="text-xs border px-3 py-1 rounded" onClick={() => navigator.clipboard.writeText(buildScanEmbed(embedFor.qrCodeSlug))}>Copy Komplett</button>
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] text-muted-foreground mb-1">Nur Registrierung (ohne Token/Code‑Ansicht)</div>
                      <textarea readOnly className="w-full h-28 border rounded p-2 text-[11px] font-mono" value={buildScanRegisterOnlyEmbed(embedFor.qrCodeSlug)} />
                      <div className="mt-2 flex gap-2">
                        <button className="text-xs border px-3 py-1 rounded" onClick={() => navigator.clipboard.writeText(buildScanRegisterOnlyEmbed(embedFor.qrCodeSlug))}>Copy Nur Registrierung</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {embedTab === 'redeem' && (
                <div>
                  <div className="text-xs font-semibold mb-1">Einlösen Formular (generisch)</div>
                  <textarea readOnly className="w-full h-28 border rounded p-2 text-[11px] font-mono" value={buildRedeemEmbed()} />
                  <div className="mt-2"><button className="text-xs border px-3 py-1 rounded" onClick={() => navigator.clipboard.writeText(buildRedeemEmbed())}>Copy Redeem Embed</button></div>
                </div>
              )}
              {embedTab === 'verify' && (
                <div>
                  <div className="text-xs font-semibold mb-1">Code anzeigen (via Token‑Link)</div>
                  <textarea readOnly className="w-full h-28 border rounded p-2 text-[11px] font-mono" value={buildVerifyEmbedTemplate()} />
                  <div className="mt-2"><button className="text-xs border px-3 py-1 rounded" onClick={() => navigator.clipboard.writeText(buildVerifyEmbedTemplate())}>Copy Verify Embed</button></div>
                </div>
              )}
              {embedTab === 'claim' && (
                <div>
                  <div className="text-xs font-semibold mb-1">Code anzeigen (direkt per Code)</div>
                  <textarea readOnly className="w-full h-28 border rounded p-2 text-[11px] font-mono" value={buildClaimEmbedTemplate()} />
                  <div className="mt-2"><button className="text-xs border px-3 py-1 rounded" onClick={() => navigator.clipboard.writeText(buildClaimEmbedTemplate())}>Copy Claim Embed</button></div>
                </div>
              )}
              {embedTab === 'qr' && (
                <div>
                  <div className="text-xs font-semibold mb-1">QR‑Bild zum Formular</div>
                  <textarea readOnly className="w-full h-24 border rounded p-2 text-[11px] font-mono" value={buildQrImageEmbed(embedFor.qrCodeSlug)} />
                  <div className="mt-2"><button className="text-xs border px-3 py-1 rounded" onClick={() => navigator.clipboard.writeText(buildQrImageEmbed(embedFor.qrCodeSlug))}>Copy QR‑Bild Embed</button></div>
                </div>
              )}
              {embedTab === 'link' && (
                <div>
                  <div className="text-xs font-semibold mb-1">Direkter Formular‑Link</div>
                  <textarea readOnly className="w-full h-20 border rounded p-2 text-[11px] font-mono" value={buildFormLinkEmbed(embedFor.qrCodeSlug)} />
                  <div className="mt-2"><button className="text-xs border px-3 py-1 rounded" onClick={() => navigator.clipboard.writeText(buildFormLinkEmbed(embedFor.qrCodeSlug))}>Copy Link Embed</button></div>
                </div>
              )}
              <p className="text-[11px] text-muted-foreground">
                Hinweis: Die Seite muss über http/https geladen werden (kein <code className="font-mono">file://</code>), sonst blockt die CSP. Iframes passen ihre Höhe automatisch per postMessage an.
              </p>
            </div>
          </div>
        </div>
      )}

      {editing && editForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={closeEdit}>
          {/* Limit modal height to viewport and allow internal scrolling */}
          <form onSubmit={submitEdit} className="bg-background p-6 rounded shadow max-w-2xl w-full space-y-4 max-h-[85vh] overflow-y-auto" onClick={(e)=> e.stopPropagation()}>
            <h4 className="font-semibold text-sm">{t('merchant.actions.edit') || 'Edit Action'}</h4>
            {/* Campaign timeframe guard and disclaimer */}
            {(() => {
              const camp = campaigns.find(c => c.id === editing.campaignId);
              const cStart = camp?.startsAt ? new Date(camp.startsAt) : null;
              const cEnd = camp?.endsAt ? new Date(camp.endsAt) : null;
              const aStart = editForm.actionStart ? new Date(editForm.actionStart) : null;
              const aEnd = editForm.actionEnd ? new Date(editForm.actionEnd) : null;
              const tooEarly = !!(cStart && aStart && aStart < cStart);
              const tooLate = !!(cEnd && aEnd && aEnd > cEnd);
              const out = tooEarly || tooLate;
              const fmt = (d) => d ? new Date(d).toLocaleString() : '-';
              return out ? (
                <div className="p-2 border border-red-300 bg-red-50 text-red-800 rounded text-[11px]">
                  {t('merchant.actions.create.s3.errors.adjustPeriod') || 'Period outside campaign range.'}
                  {tooEarly && (
                    <div>{t('merchant.actions.create.s3.errors.beforeCampaignStart', { date: fmt(cStart) })}</div>
                  )}
                  {tooLate && (
                    <div>{t('merchant.actions.create.s3.errors.afterCampaignEnd', { date: fmt(cEnd) })}</div>
                  )}
                </div>
              ) : null;
            })()}
            <div className="grid md:grid-cols-2 gap-3 text-xs items-end">
              <div className="space-y-1">
                <label className="text-[11px] text-muted-foreground">{t('common.title')}</label>
                <input className="border px-2 py-1 rounded w-full" value={editForm.title} onChange={(e)=>setEditForm(f=>({...f, title:e.target.value}))} required />
              </div>
              <div className="space-y-1">
                <label className="text-[11px] text-muted-foreground">Slug</label>
                <input className="border px-2 py-1 rounded w-full font-mono bg-muted" value={editing.qrCodeSlug} readOnly />
              </div>
              <div className="space-y-1">
                <label className="text-[11px] text-muted-foreground">Rabatt-Typ</label>
                <select className="border px-2 py-1 rounded w-full" value={editForm.discountType} onChange={(e)=>setEditForm(f=>({...f, discountType:e.target.value}))}>
                  <option value="PERCENT">Prozent</option>
                  <option value="AMOUNT">Betrag</option>
                  <option value="UNIT">Stück</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-[11px] text-muted-foreground">Rabatt-Wert</label>
                <input type="number" step="0.01" className="border px-2 py-1 rounded w-full" value={editForm.discountValue} onChange={(e)=>setEditForm(f=>({...f, discountValue:e.target.value}))} />
              </div>
              <div className="space-y-1">
                <label className="text-[11px] text-muted-foreground">Max Einlösungen</label>
                <input type="number" className="border px-2 py-1 rounded w-full" value={editForm.maxRedemptions} onChange={(e)=>setEditForm(f=>({...f, maxRedemptions:e.target.value}))} />
              </div>
              <div className="space-y-1 md:col-span-2">
                <label className="text-[11px] text-muted-foreground">Aktionszeitraum ab/bis</label>
                <div className="grid grid-cols-2 gap-2">
                  <input type="datetime-local" className="border px-2 py-1 rounded w-full" value={editForm.actionStart} onChange={(e)=>setEditForm(f=>({...f, actionStart:e.target.value}))} />
                  <input type="datetime-local" className="border px-2 py-1 rounded w-full" value={editForm.actionEnd} onChange={(e)=>setEditForm(f=>({...f, actionEnd:e.target.value}))} />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-[11px] text-muted-foreground">Ziel Scans</label>
                <input type="number" className="border px-2 py-1 rounded w-full" value={editForm.targetScans} onChange={(e)=>setEditForm(f=>({...f, targetScans:e.target.value}))} />
              </div>
              <div className="space-y-1">
                <label className="text-[11px] text-muted-foreground">Ziel Einlösungen</label>
                <input type="number" className="border px-2 py-1 rounded w-full" value={editForm.targetRedeems} onChange={(e)=>setEditForm(f=>({...f, targetRedeems:e.target.value}))} />
              </div>
            </div>
            <div className="border-t pt-3">
              <div className="flex items-center justify-between">
                <h5 className="text-xs font-semibold">Meta Conversions API</h5>
                <label className="text-xs inline-flex items-center gap-2">
                  <input type="checkbox" checked={!!editForm.capiEnabled} onChange={(e)=>setEditForm(f=>({...f, capiEnabled: e.target.checked}))} />
                  Aktiviert
                </label>
              </div>
              <div className="grid md:grid-cols-2 gap-3 mt-2">
                <div className="space-y-1">
                  <label className="text-[11px] text-muted-foreground">Pixel Override (optional)</label>
                  <input className="border px-2 py-1 rounded w-full" placeholder="überschreibt Company Pixel ID" value={editForm.capiPixelIdOverride} onChange={(e)=>setEditForm(f=>({...f, capiPixelIdOverride:e.target.value}))} />
                </div>
                <div className="space-y-1">
                  <label className="text-[11px] text-muted-foreground">Test Code Override (optional)</label>
                  <input className="border px-2 py-1 rounded w-full" placeholder="überschreibt Company Test Code" value={editForm.capiTestCodeOverride} onChange={(e)=>setEditForm(f=>({...f, capiTestCodeOverride:e.target.value}))} />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-3 mt-2">
                <div className="space-y-1">
                  <label className="text-[11px] text-muted-foreground">Conversion-Wert Override</label>
                  <input type="number" step="0.01" className="border px-2 py-1 rounded w-full" placeholder="z. B. 5.00" value={editForm.capiConversionValueOverride} onChange={(e)=>setEditForm(f=>({...f, capiConversionValueOverride:e.target.value}))} />
                </div>
                <div className="space-y-1">
                  <label className="text-[11px] text-muted-foreground">Lead-Eventname Override</label>
                  <input className="border px-2 py-1 rounded w-full" placeholder="z. B. Lead, Contact…" value={editForm.leadEventNameOverride} onChange={(e)=>setEditForm(f=>({...f, leadEventNameOverride:e.target.value}))} />
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">{t('capi.accessTokenHelpLine2')}</p>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={closeEdit} className="text-xs border px-3 py-1 rounded">{t('common.cancel')}</button>
              {(() => {
                const camp = campaigns.find(c => c.id === editing.campaignId);
                const cStart = camp?.startsAt ? new Date(camp.startsAt) : null;
                const cEnd = camp?.endsAt ? new Date(camp.endsAt) : null;
                const aStart = editForm.actionStart ? new Date(editForm.actionStart) : null;
                const aEnd = editForm.actionEnd ? new Date(editForm.actionEnd) : null;
                const tooEarly = !!(cStart && aStart && aStart < cStart);
                const tooLate = !!(cEnd && aEnd && aEnd > cEnd);
                const disabled = tooEarly || tooLate;
                return (
                  <button className="text-xs bg-blue-600 text-white px-3 py-1 rounded disabled:opacity-50" type="submit" disabled={disabled}>{t('common.save')}</button>
                );
              })()}
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ActionsManager;
